import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/fields/index.js';
import '../../library/elements/d-section.js';
import '../../library/editors/index.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/editors/components/d-edit-classification.js';
import { customElement } from 'lit/decorators.js';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { DocumentViewEditItem } from './document-view-edit-item';
import type { DocumentView } from './document-view';
import type { UpdateSectionItem } from '../update-section-item';
import type { ListDefinition } from '../list-definition';
import type { SectionField } from '../../library/components/section-field';

/**
 *
 * STATUS OK
 */
@customElement('d-document-view')
export class DDocumentView extends EntityContent<DocumentView, DocumentViewEditItem> {
  static readonly styles = EntityContent.styles;

  renderViewMode() {
    return html` ${this.renderFieldSection('documents', this.viewFields(), this.entityView)} `;
  }

  async initializeEditItem() {
    if (this.entityView.hasDraft) {
      this.editItem = await this.entityView.fetchDraft();
    } else {
      this.editItem = {
        accessControl: this.entityView.accessControl,
        classification: this.entityView.classification,
        content: this.entityView.content,
        validFromDate: this.entityView.validFromDate,
        validToDate: this.entityView.validToDate,
        name: this.entityView.name,
        pages: this.entityView.pages,
      };
    }
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: DocumentViewEditItem): TemplateResult<1> {
    return html`
      ${this.renderEditClassification(item)} ${this.renderFieldSection('documents', this.editFields(), item)}
      <d-edit-html
        field="documents_content"
        .value=${item.content}
        .docsForLinking=${this.entityView.docsForLinking}
        .contentStickyTop=${this.contentStickyTop}
        @value-changed=${(e: CustomEvent<{ value: string }>) => {
          this.editItem = { ...item, content: e.detail.value };
        }}
      ></d-edit-html>
    `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'classificationText',
        fieldForLabel: 'generalFields_access',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.classificationText !== '',
      },
      {
        field: 'validFromDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
      },
      {
        field: 'validToDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
      },
      {
        field: 'content',
        hideLabel: true,
        type: 'view-html',
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private editFields(): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        placeholder: t('reports.fieldMustBeFilledOut'),
        autofocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        class: 'fullWidth',
      },
      {
        field: 'validFromDate',
        type: 'edit-date',
        class: 'minWidth300',
      },
      {
        field: 'validToDate',
        type: 'edit-date',
        class: 'minWidth300',
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-document-view': DDocumentView;
  }
}
